/**
 * Content Target Schemas
 *
 * Discriminated union for content publishing targets.
 * Entity-referencing targets (group, person, home_screen) carry an `id`.
 * Section targets carry `sectionId` plus a target-shape `audience` enum
 * (the U7 resolver expands `audience: 'ALL'` to a union of the four
 * concrete audiences). Virtual targets (app_home) are resolved at
 * pipeline time and carry config instead.
 *
 * U4 (2026-05-11) — Compound audience model:
 *   - `SectionTarget` gains optional `audience_tags: string[]` alongside
 *     the legacy `audience` enum. `audience_tags` is the compound
 *     primitive; the legacy enum is retained for back-compat and
 *     normalized via `audienceEnumToTags()` when `audience_tags` is
 *     unset or empty. Use `getEffectiveAudienceTags(target)` to read.
 *   - New `ListTarget` (`{type:'list', listId}`) and `AppMessageTarget`
 *     (`{type:'app_message', audience_tags}`) variants per KTD-6.
 *
 * Source of truth for the `audience:` namespace is `SectionMembership.role`
 * (A-decision-3) — never `UsersOnSchools` identity flags. See
 * docs/solutions/communications/compound-audience-tag-model.md.
 */

import { z } from 'zod'

// --- Entity-referencing targets (have id) ---

export const GroupTargetSchema = z.object({
  type: z.literal('group'),
  id: z.string(),
  isPrimary: z.boolean().optional()
})

export const PersonTargetSchema = z.object({
  type: z.literal('person'),
  id: z.string(),
  isPrimary: z.boolean().optional()
})

export const HomeScreenTargetSchema = z.object({
  type: z.literal('home_screen'),
  id: z.string(),
  isPrimary: z.boolean().optional()
})

// --- Section target (U6a of section-canonical cutover). ---
// Carries sectionId + a target-shape audience enum that includes 'ALL'.
// The resolver in U7 expands 'ALL' to a union of {STUDENTS, PARENTS,
// FACULTY, STAFF}; the four concrete values are also what persists on
// the `SectionAudience` Prisma enum / `SectionAudienceConfig.audience`
// — distinct concept from the `audiences` plural array on
// AppHomeTargetConfig below.

export const SectionTargetAudienceSchema = z.enum(['ALL', 'STUDENTS', 'PARENTS', 'FACULTY', 'STAFF'])

export const SectionTargetSchema = z.object({
  type: z.literal('section'),
  sectionId: z.string(),
  audience: SectionTargetAudienceSchema,
  // U4 — compound audience tags. When unset or empty, the legacy `audience`
  // enum is normalized via `audienceEnumToTags()` at read time
  // (`getEffectiveAudienceTags`). Stored as raw `audience:{ROLE}` /
  // `school_level:{ABBR}` / `grade_level:{ABBR}` strings per the
  // compound-audience-tag-model doc.
  audience_tags: z.array(z.string()).optional(),
  isPrimary: z.boolean().optional()
})

// --- List target (KTD-6 / U4) ---
// Future surface for ad-hoc user lists (e.g., AdvancedListMembership groups).
// Carries the list's public id; resolver maps to `group:{listId}` filter_tag.
// No audience cut — list membership IS the audience.

export const ListTargetSchema = z.object({
  type: z.literal('list'),
  listId: z.string(),
  isPrimary: z.boolean().optional()
})

// --- App Message target (KTD-6 / U4) ---
// School-wide pinned activity (App Messages refactored from a separate
// surface into compound-audience activities in U9). NOT section-scoped —
// `audience_tags` carry the full cut (e.g., school_level + audience role).
// `pinned` semantics live on the activity body, not the target.

export const AppMessageTargetSchema = z.object({
  type: z.literal('app_message'),
  audience_tags: z.array(z.string()).optional(),
  isPrimary: z.boolean().optional()
})

// --- Virtual targets (no id, resolved at pipeline time) ---

export const AppHomeTargetConfigSchema = z.object({
  audiences: z
    .array(z.enum(['PARENTS', 'STUDENTS', 'FACULTY']))
    .min(1)
    .optional()
    .default(['PARENTS', 'STUDENTS', 'FACULTY']),
  priority: z.enum(['normal', 'high', 'low']).optional().default('normal'),
  dismissible: z.boolean().optional().default(true),
  appLocation: z.enum(['home_screen', 'assignments', 'calendar', 'notifications']).optional().default('home_screen')
})

export const AppHomeTargetSchema = z.object({
  type: z.literal('app_home'),
  config: AppHomeTargetConfigSchema.optional()
})

// --- Discriminated union ---

export const ContentTargetSchema = z.discriminatedUnion('type', [
  GroupTargetSchema,
  PersonTargetSchema,
  HomeScreenTargetSchema,
  SectionTargetSchema,
  ListTargetSchema,
  AppMessageTargetSchema,
  AppHomeTargetSchema
])

// --- Entity target union (targets with an id) ---

export const EntityTargetSchema = z.union([GroupTargetSchema, PersonTargetSchema, HomeScreenTargetSchema])

// --- Inferred types ---

export type GroupTarget = z.infer<typeof GroupTargetSchema>
export type PersonTarget = z.infer<typeof PersonTargetSchema>
export type HomeScreenTarget = z.infer<typeof HomeScreenTargetSchema>
export type SectionTargetAudience = z.infer<typeof SectionTargetAudienceSchema>
export type SectionTarget = z.infer<typeof SectionTargetSchema>
export type ListTarget = z.infer<typeof ListTargetSchema>
export type AppMessageTarget = z.infer<typeof AppMessageTargetSchema>
export type AppHomeTargetConfig = z.infer<typeof AppHomeTargetConfigSchema>
export type AppHomeTarget = z.infer<typeof AppHomeTargetSchema>
export type ContentTarget = z.infer<typeof ContentTargetSchema>

/** Entity targets carry an `id` field (group, person, home_screen) */
export type EntityTarget = z.infer<typeof EntityTargetSchema>

/** Type guard: narrows ContentTarget to EntityTarget (has id).
 *
 * **Section targets do NOT match this guard.** SectionTarget carries
 * `sectionId`, not `id`. Call sites that genuinely want to operate on
 * both entity-targets and section-targets should use `isSectionTarget`
 * + the `EntityOrSectionTarget` union (below) — opt-in widening,
 * preserves existing call-site behavior. See KTD-4 in
 * docs/_archive/2026-10-17-plans-sweep/plans/2026-05-09-004-feat-groups-section-canonical-plan.md.
 */
export function isEntityTarget(target: ContentTarget): target is EntityTarget {
  return 'id' in target
}

// --- Section target helpers (U6b of section-canonical cutover) ---

/**
 * Type guard: narrows ContentTarget to SectionTarget (has sectionId).
 *
 * Parallel to `isEntityTarget`. The two guards together cover every
 * id-bearing variant (entity targets carry `id`; section targets carry
 * `sectionId`). Used by call sites that want to treat both as
 * "addressable" without conflating their schemas.
 */
export function isSectionTarget(target: ContentTarget): target is SectionTarget {
  return target.type === 'section'
}

/** Union of every variant that addresses a specific concrete target —
 * either by entity `id` (group / person / home_screen) or by `sectionId`.
 * Excludes the virtual `app_home` variant.
 */
export const EntityOrSectionTargetSchema = z.union([
  GroupTargetSchema,
  PersonTargetSchema,
  HomeScreenTargetSchema,
  SectionTargetSchema
])
export type EntityOrSectionTarget = z.infer<typeof EntityOrSectionTargetSchema>

/** Type guard: narrows ContentTarget to EntityOrSectionTarget. */
export function isEntityOrSectionTarget(target: ContentTarget): target is EntityOrSectionTarget {
  return isEntityTarget(target) || isSectionTarget(target)
}

/**
 * Find the primary target in a list, supporting both entity-targets
 * (have `id`) and section-targets (have `sectionId`). Replaces ad-hoc
 * `'id' in t` checks (e.g. target-comparison.ts pre-U6b) that miss
 * section variants.
 */
export function findPrimaryEntityOrSectionTarget(targets: readonly ContentTarget[]): EntityOrSectionTarget | undefined {
  return targets.filter(isEntityOrSectionTarget).find(t => t.isPrimary === true)
}

// --- U4: compound-audience tag helpers ---

/**
 * Normalize the legacy `SectionTargetAudience` enum to the compound
 * `audience:` tag namespace. `'ALL'` → `[]` (no audience cut beyond the
 * section itself); singular enums → one `audience:{ROLE}` tag.
 *
 * Source of truth note: the resulting tags live in the `audience:`
 * namespace which is sourced from `SectionMembership.role` at resolve
 * time — never from `UsersOnSchools` identity flags. See
 * docs/solutions/communications/compound-audience-tag-model.md
 * (A-decision-3, 2026-05-11).
 */
export function audienceEnumToTags(audience: SectionTargetAudience): string[] {
  switch (audience) {
    case 'ALL':
      return []
    case 'STUDENTS':
      return ['audience:STUDENT']
    case 'PARENTS':
      return ['audience:PARENT']
    case 'FACULTY':
      return ['audience:FACULTY']
    case 'STAFF':
      return ['audience:STAFF']
  }
}

/**
 * Read the compound `audience_tags` set for a tag-bearing target,
 * normalizing the legacy `audience` enum on `SectionTarget` when
 * `audience_tags` is unset or empty. Single chokepoint so consumers
 * (e.g., `composeFilterTags`) never branch on the legacy field.
 *
 * Empty array result = no audience cut (activity tagged with the
 * section feed name only, visible to every follower regardless of role).
 */
export function getEffectiveAudienceTags(target: SectionTarget | AppMessageTarget): string[] {
  if (target.type === 'app_message') {
    return target.audience_tags ?? []
  }
  // SectionTarget: explicit audience_tags wins; empty/unset → legacy enum.
  if (target.audience_tags && target.audience_tags.length > 0) {
    return target.audience_tags
  }
  return audienceEnumToTags(target.audience)
}

/** Type guard: narrows ContentTarget to ListTarget. */
export function isListTarget(target: ContentTarget): target is ListTarget {
  return target.type === 'list'
}

/** Type guard: narrows ContentTarget to AppMessageTarget. */
export function isAppMessageTarget(target: ContentTarget): target is AppMessageTarget {
  return target.type === 'app_message'
}
